import { useEffect, useRef, useState } from "react";

const testimonials = [
  {
    name: "Maria Gonzalez",
    role: "Mother of 3, Irving TX",
    stars: 5,
    quote:
      "I called HealthMarkets at 11pm on a Sunday, completely stressed about our family plan expiring. Not only did someone answer right away, but they walked me through three options and we enrolled the same night. Absolutely incredible service!",
    avatar:
      "https://public.readdy.ai/ai/img_res/a4c6edd06b81822801739188af710faf.jpg",
  },
  {
    name: "James Whitfield",
    role: "Small Business Owner, Dallas TX",
    stars: 5,
    quote:
      "Running a small business, finding group health insurance used to be a nightmare. HealthMarkets compared over 20 plans for my team and saved us nearly $400 a month. Our employees are thrilled with the coverage.",
    avatar:
      "https://readdy.ai/api/search-image?query=professional%20African%20American%20man%20in%20his%2040s%20business%20headshot%2C%20confident%20warm%20smile%2C%20clean%20light%20background%2C%20natural%20lighting%2C%20professional%20corporate%20portrait%20photography%20style&width=80&height=80&seq=test002&orientation=squarish",
  },
  {
    name: "Sandra Chen",
    role: "Retired Teacher, Irving TX",
    stars: 5,
    quote:
      "Navigating Medicare was so confusing until I found HealthMarkets. My agent spent over an hour explaining the difference between Advantage and Supplement plans. I finally understand my coverage and I'm saving $180/month!",
    avatar:
      "https://readdy.ai/api/search-image?query=friendly%20Asian%20woman%20in%20her%2060s%20warm%20headshot%20portrait%2C%20silver%20hair%2C%20bright%20smile%2C%20soft%20natural%20lighting%2C%20clean%20neutral%20background%2C%20approachable%20senior%20professional%20portrait&width=80&height=80&seq=test003&orientation=squarish",
  },
  {
    name: "David Martinez",
    role: "Freelancer, Las Colinas TX",
    stars: 5,
    quote:
      "As a self-employed graphic designer, I had no clue what individual health plan was right for me. HealthMarkets made it easy — they looked at my income, health needs, and budget and found the perfect ACA plan with great subsidies.",
    avatar:
      "https://readdy.ai/api/search-image?query=young%20Latino%20man%20in%20his%2030s%20casual%20professional%20headshot%2C%20friendly%20smile%2C%20warm%20lighting%2C%20clean%20background%2C%20modern%20creative%20professional%20portrait%20photography&width=80&height=80&seq=test004&orientation=squarish",
  },
  {
    name: "Patricia Okafor",
    role: "Nurse, Irving TX",
    stars: 5,
    quote:
      "Even as a nurse I was overwhelmed by insurance options for my family. The HealthMarkets team in Irving is so knowledgeable and patient. They found us dental, vision, and health all bundled from the same carrier. Two thumbs up!",
    avatar:
      "https://readdy.ai/api/search-image?query=professional%20African%20American%20woman%20in%20her%2040s%20warm%20headshot%2C%20bright%20smile%2C%20medical%20professional%20look%2C%20clean%20light%20background%2C%20natural%20professional%20portrait%20photography%20style&width=80&height=80&seq=test005&orientation=squarish",
  },
  {
    name: "Robert & Linda Kim",
    role: "Retired Couple, Coppell TX",
    stars: 5,
    quote:
      "We were both turning 65 the same year and had no idea how to enroll in Medicare together. HealthMarkets handled everything — separate enrollment, plan comparisons, even set up our prescription coverage. Couldn't be happier!",
    avatar:
      "https://readdy.ai/api/search-image?query=happy%20senior%20Asian%20couple%20in%20their%2060s%20smiling%20portrait%2C%20retirement%20lifestyle%2C%20warm%20natural%20light%2C%20clean%20background%2C%20joyful%20mature%20couple%20headshot%20photography&width=80&height=80&seq=test006&orientation=squarish",
  },
];

export default function TestimonialsSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="testimonials" className="py-24 bg-amber-50" ref={ref}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className={`text-center mb-14 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <span className="inline-block px-4 py-1.5 rounded-full bg-amber-200 text-amber-800 text-xs font-['Open_Sans'] font-600 tracking-widest uppercase mb-4">
            Client Stories
          </span>
          <h2 className="font-['Poppins'] font-700 text-4xl lg:text-5xl text-emerald-900 mb-4">
            What Our Clients Say
          </h2>
          <div className="flex items-center justify-center gap-2 mb-3">
            {[...Array(5)].map((_, i) => (
              <i key={i} className="ri-star-fill text-amber-500 text-xl"></i>
            ))}
            <span className="font-['Open_Sans'] text-gray-600 text-sm ml-2">Rated 5.0 by 200+ clients</span>
          </div>
          <p className="font-['Open_Sans'] text-gray-500 max-w-lg mx-auto text-base leading-relaxed">
            Real stories from real families we've helped find the right <strong>private health insurance</strong> plan in Irving, TX.
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <div
              key={t.name}
              className={`bg-white border border-amber-100 rounded-lg p-6 transition-all duration-700 ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              {/* Stars */}
              <div className="flex gap-1 mb-4">
                {[...Array(t.stars)].map((_, si) => (
                  <i key={si} className="ri-star-fill text-amber-500 text-sm"></i>
                ))}
              </div>

              {/* Quote icon */}
              <div className="w-8 h-8 flex items-center justify-center mb-3">
                <i className="ri-double-quotes-l text-4xl text-emerald-200"></i>
              </div>

              {/* Quote */}
              <p className="font-['Open_Sans'] text-gray-600 text-sm leading-relaxed mb-6 italic">
                "{t.quote}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-3 pt-4 border-t border-amber-100">
                <div className="w-11 h-11 rounded-full overflow-hidden flex-shrink-0">
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div>
                  <div className="font-['Poppins'] font-600 text-emerald-900 text-sm">{t.name}</div>
                  <div className="font-['Open_Sans'] text-gray-400 text-xs">{t.role}</div>
                </div>
                <div className="ml-auto flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200">
                  <i className="ri-verified-badge-fill text-emerald-600 text-xs"></i>
                  <span className="text-xs font-['Open_Sans'] text-emerald-700 font-600">Verified</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
